import { GridColDef } from '@mui/x-data-grid-pro';
import GpanelContactsTrashPage from '../gpanel-contacts-trash-page';
import { useTrashContactsQuery } from '../../../gpanel-contacts-queries';
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';
import { useGpanelContactsPermissions } from '../../../shared/hooks/use-gpanel-contacts-permissions';
import { GPanelContactsPermissions } from '../../../../../../utils/roles/client-roles';
import { useState } from 'react';
import { Contact } from '../../../shared/types';
import GpanelContactsRecoverContactsDialog from './gpanel-contacts-recover-contacts-dialog';
import GpanelContactsEmptyContactsTrashDialog from './gpanel-contacts-empty-contacts-trash-dialog';
import { getDateTimeString } from '../../../shared/utils.ts';
import { useUserSettings } from '../../../../../../utils/use-user-settings.tsx';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

dayjs.extend(relativeTime);

const columns: GridColDef<Contact>[] = [
  {
    field: 'contactName',
    headerName: 'Contact Name',
    flex: 1,
    renderCell: (param) => {
      return (
        <Typography variant="body2">{`${param.row.firstName ?? ''} ${
          param.row.lastName ?? ''
        }`}</Typography>
      );
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 1,
    valueGetter: (_value, row) => {
      return row.emails?.[0]?.value ?? '';
    },
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone Number',
    flex: 1,
    valueGetter: (_value, row) => {
      return row.phoneNumbers?.[0]?.value ?? '';
    },
  },
  {
    field: 'dateDeleted',
    headerName: 'Date Trashed',
    flex: 1,
    valueGetter: (_value, _row) => {
      return getDateTimeString(_row?.trashedTime ?? 0);
    },
  },
  {
    field: 'dateToDeleted',
    headerName: 'Date to be Deleted',
    flex: 1,
    valueGetter: (_value, _row) => {
      return _row?.trashedTime
        ? getDateTimeString(dayjs(_row.trashedTime).add(30, 'day').valueOf())
        : getDateTimeString(0);
    },
  },
  {
    field: 'labels',
    headerName: 'Labels',
    flex: 1,
    sortable: false,
    valueGetter: (_value, row) => row.activeLabels?.join(', ') ?? '',
    renderCell: ({ row }) =>
      row.activeLabels?.length ? (
        <Stack flexDirection="row" gap={1} sx={{ overflowX: 'auto' }}>
          {row.activeLabels.map((label: string) => (
            <Chip
              key={label}
              label={label}
              size="small"
              variant="outlined"
              sx={(theme) => ({
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
              })}
            />
          ))}
        </Stack>
      ) : null,
  },
];

export default function GpanelContactsTrashContacts() {
  const { PERMISSIONS } = useGpanelContactsPermissions();
  const query = useTrashContactsQuery();
  const [selectedContactIds, setSelectedContactIds] = useState<number[] | null>(
    null
  );
  const [isEmptyModalOpened, setEmptyModalOpened] = useState<boolean>(false);
  const { viewType } = useUserSettings();

  return (
    <>
      <GpanelContactsTrashPage
        title="Contacts"
        query={query}
        gridKey={viewType.trashedContactsGridStates?.activeGridState}
        columns={columns}
        recoverPermission={{
          value: PERMISSIONS.recoverTrashedContacts,
          name: GPanelContactsPermissions.RecoverTrashedContacts.name,
        }}
        emptyPermission={{
          value: PERMISSIONS.emptyTrashedContacts,
          name: GPanelContactsPermissions.EmptyTrashedContacts.name,
        }}
        onRecover={(model) => {
          setSelectedContactIds(model as number[]);
        }}
        onEmpty={() => {
          setEmptyModalOpened(true);
        }}
      />
      {selectedContactIds && (
        <GpanelContactsRecoverContactsDialog
          handleClose={() => setSelectedContactIds(null)}
          contactsIds={selectedContactIds}
        />
      )}
      {isEmptyModalOpened && (
        <GpanelContactsEmptyContactsTrashDialog
          handleClose={() => setEmptyModalOpened(false)}
        />
      )}
    </>
  );
}
